import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { breakpoints, Column, Columns, Stack } from '@etg/wings';
import { ContentWrapper } from '@eti/components';
import { EventSegmentType } from '@eti/schema-types';
import { FAST_TRACK } from '@eti/products';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import AppBanner from '../common/AppBanner';
import TripEventsCards from '../common/TripEventsCards';
import { useRoutedTabs } from '../../../../widgets/tabs/components/RoutedTabs';
import FaqSection from '../common/FaqSection';
import ImportantInfo from './ImportantInfo';
import NotPurchasedCheckIn from './not-purchased/NotPurchasedCheckIn';
import FastTrackBanner from './FastTrackBanner';
import PurchasedCheckIn from './purchased-checkin/PurchasedCheckIn';

const contentWrapperStyles = css`
  margin-bottom: 40px;
  margin-top: 16px;

  @media (min-width: ${breakpoints._768}) {
    margin-top: 24px;
  }
`;

const OrderDetailsCheckIn = () => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { agencyAccount } = useSiteContext();
  const { tabHeading1Ref } = useRoutedTabs();
  const {
    availableExtraProducts,
    eventTypes,
    faqSectionPerSlug,
    isCheckInPurchased,
    isWhiteLabelBrand,
  } = useOrderDetails();
  const shouldShowAppBanner =
    !agencyAccount && p('Order.DownloadApp.Enabled') && !isWhiteLabelBrand;
  const shouldShowSelfTransferCard = eventTypes.includes(EventSegmentType.SelfTransfer);
  const shouldShowFastTrackBanner =
    isCheckInPurchased &&
    availableExtraProducts &&
    availableExtraProducts.some((product) => Number(product.id) === FAST_TRACK.id);
  const faqSectionSlug = p('OrderDetails.CheckIn.Faq.Slug');

  return (
    <ContentWrapper className={contentWrapperStyles}>
      <h1 className={screenReadersOnlyStyles} ref={tabHeading1Ref} tabIndex={-1}>
        {t('OrderDetails.Tab.CheckIn.Description')}
      </h1>
      <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
        <Columns
          collapseBelow={breakpoints._768}
          spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}
        >
          <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '7/12' }}>
            <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
              {isCheckInPurchased ? <PurchasedCheckIn /> : <NotPurchasedCheckIn />}
              {shouldShowSelfTransferCard && (
                <TripEventsCards
                  eventTypes={[EventSegmentType.SelfTransfer]}
                  shouldAddBorder={false}
                />
              )}
              {shouldShowFastTrackBanner && <FastTrackBanner />}
            </Stack>
          </Column>
          <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '5/12' }}>
            <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
              {shouldShowAppBanner && <AppBanner />}
              <ImportantInfo />
              {faqSectionPerSlug?.[faqSectionSlug] && (
                <FaqSection section={faqSectionPerSlug[faqSectionSlug]} shouldRedirectToContactUs />
              )}
            </Stack>
          </Column>
        </Columns>
      </Stack>
    </ContentWrapper>
  );
};

export default OrderDetailsCheckIn;
